import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const PracticeContext = createContext(null);
const SELECTED_PRACTICE_KEY = 'selectedPracticeId';

export function PracticeProvider({ children }) {
  const { isAuthenticated, user, switchPractice: authSwitchPractice, isSuperAdmin } = useAuth();
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  // Load onboarding status for the selected practice
  const loadOnboardingStatus = useCallback(async (practiceId) => {
    if (!practiceId || !isAuthenticated) {
      setOnboardingStatus(null);
      return;
    }
    try {
      const response = await api.getOnboardingStatus(practiceId);
      if (response.success) {
        setOnboardingStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
      setOnboardingStatus(null);
    }
  }, [isAuthenticated]);

  // Load practices when authenticated
  const loadPractices = useCallback(async () => {
    if (!isAuthenticated) {
      setPractices([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.get('/practices');
      if (response.success) {
        setPractices(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load practices:', error);
      setPractices([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Load practices on authentication change
  useEffect(() => {
    loadPractices();
  }, [loadPractices]);

  // Initialize selectedPracticeId from JWT context or localStorage
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // For non-super-admin: use activePracticeId from JWT context
    const jwtPracticeId = user?.activeContext?.activePracticeId;
    if (jwtPracticeId) {
      setSelectedPracticeId(jwtPracticeId);
      localStorage.setItem(SELECTED_PRACTICE_KEY, jwtPracticeId.toString());
      return;
    }

    // Fallback: load from localStorage (for super admin or legacy)
    const savedPracticeId = localStorage.getItem(SELECTED_PRACTICE_KEY);
    if (savedPracticeId) {
      setSelectedPracticeId(parseInt(savedPracticeId, 10));
    }
  }, [isAuthenticated, user]);

  // Update selected practice when practices load or selection changes
  useEffect(() => {
    if (practices.length > 0) {
      // If we have a selected practice ID, find it
      if (selectedPracticeId) {
        const practice = practices.find(p => p.id === selectedPracticeId);
        if (practice) {
          setSelectedPractice(practice);
        } else {
          // Selected practice not accessible, reset to first available
          setSelectedPracticeId(practices[0].id);
          setSelectedPractice(practices[0]);
          localStorage.setItem(SELECTED_PRACTICE_KEY, practices[0].id.toString());
        }
      } else {
        // No selection, select first practice
        setSelectedPracticeId(practices[0].id);
        setSelectedPractice(practices[0]);
        localStorage.setItem(SELECTED_PRACTICE_KEY, practices[0].id.toString());
      }
    } else {
      setSelectedPractice(null);
    }
  }, [practices, selectedPracticeId]);

  // Load onboarding status when selected practice changes
  useEffect(() => {
    if (selectedPracticeId) {
      loadOnboardingStatus(selectedPracticeId);
    }
  }, [selectedPracticeId, loadOnboardingStatus]);

  const needsOnboarding = onboardingStatus && !onboardingStatus.completed;

  // Select a practice — calls backend switchPractice for non-super-admin
  const selectPractice = async (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;

    // Update JWT with new practice context
    const result = await authSwitchPractice(practiceId);
    if (result.success || isSuperAdmin()) {
      setSelectedPracticeId(practiceId);
      setSelectedPractice(practice);
      localStorage.setItem(SELECTED_PRACTICE_KEY, practiceId.toString());
      // Force page reload to clear all cached data from previous practice
      window.location.reload();
    }

    return result;
  };

  // Clear selection (for logout)
  const clearSelection = () => {
    setSelectedPracticeId(null);
    setSelectedPractice(null);
    localStorage.removeItem(SELECTED_PRACTICE_KEY);
  };

  // Get practices grouped by practice group
  const getPracticesByGroup = () => {
    const groups = {};
    practices.forEach(practice => {
      const groupName = practice.practice_group_name || 'Ungrouped';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(practice);
    });
    return groups;
  };

  // Check if user can access a specific practice
  const canAccessPractice = (practiceId) => {
    return practices.some(p => p.id === practiceId);
  };

  const value = {
    practices,
    selectedPracticeId,
    selectedPractice,
    isLoading,
    selectPractice,
    clearSelection,
    loadPractices,
    getPracticesByGroup,
    canAccessPractice,
    onboardingStatus,
    needsOnboarding,
    loadOnboardingStatus,
  };

  return (
    <PracticeContext.Provider value={value}>
      {children}
    </PracticeContext.Provider>
  );
}

export function usePractice() {
  const context = useContext(PracticeContext);
  if (!context) {
    throw new Error('usePractice must be used within a PracticeProvider');
  }
  return context;
}

export default PracticeContext;
